import axios, { AxiosResponse, AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import type { 
  ApiResponse, 
  LoginCredentials, 
  User, 
  BOL, 
  BOLFormData, 
  DataTableResponse,
  FileUploadResponse 
} from '../types';

// Create axios instance
const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
console.log('🔍 VITE_API_URL from env:', import.meta.env.VITE_API_URL);
console.log('🔍 Using API baseURL:', apiUrl);

const api = axios.create({
  baseURL: apiUrl,
  timeout: 30000,
  withCredentials: true, // Important for session cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens if using JWT instead of sessions
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    const message = error.response?.data?.message || error.message || 'An error occurred';
    
    // Handle different error status codes
    const status = error.response?.status;
    if (status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('authToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (status === 403) {
      toast.error('Access denied');
    } else if (status && status >= 500) {
      toast.error('Server error. Please try again later.');
    } else {
      toast.error(message);
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginCredentials): Promise<ApiResponse<User>> => {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  logout: async (): Promise<ApiResponse> => {
    const response = await api.post('/api/auth/logout');
    return response.data;
  },

  getProfile: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/api/auth/profile');
    return response.data;
  },

  checkAuth: async (): Promise<ApiResponse<User>> => {
    const response = await api.get('/api/auth/check');
    return response.data;
  },
};

// BOL API
export const bolAPI = {
  /**
   * The bill of lading as a PDF, fetched as a blob.
   *
   * Goes through the axios instance so the Bearer token is attached — the
   * endpoint is driver-scoped and will 404 for a leg that is not theirs. A
   * plain <embed src> could not carry the token.
   */
  getBOLPdf: async (id: number | string): Promise<Blob> => {
    const response = await api.get(`/api/bol/${id}/pdf`, { responseType: 'blob' });
    return response.data as Blob;
  },

  /**
   * A document attached to the BOL, fetched as a blob through the API — the
   * server checks the leg is this driver's and streams it with its managed
   * identity. Documents used to be fetched straight from public blob storage.
   */
  getDocument: async (id: number | string, fileName: string): Promise<Blob> => {
    const response = await api.get(
      `/api/bol/${id}/documents/${encodeURIComponent(fileName)}`,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },

  getBOLs: async (params?: {
    draw?: number;
    start?: number;
    length?: number;
    search?: { value: string };
    order?: Array<{ column: number; dir: 'asc' | 'desc' }>;
  }): Promise<DataTableResponse<BOL>> => {
    const response = await api.get('/api/bol', { params });
    return response.data;
  },

  /**
   * Mark a stop as this driver's next, or clear the designation.
   *
   * One per driver, enforced by the column it writes (drivers.nextLegId), so
   * setting a new one implicitly releases the old.
   */
  setNextStop: async (id: number, next: boolean): Promise<{ success: boolean; isNext: boolean }> => {
    const response = next
      ? await api.post(`/api/bol/${id}/next`)
      : await api.delete(`/api/bol/${id}/next`);
    return response.data;
  },

  getBOL: async (id: number): Promise<ApiResponse<BOL>> => {
    const response = await api.get(`/api/bol/${id}`);
    return response.data;
  },

  createBOL: async (data: BOLFormData): Promise<ApiResponse<BOL>> => {
    const response = await api.post('/api/bol', data);
    return response.data;
  },

  updateBOL: async (id: number, data: Partial<BOLFormData>): Promise<ApiResponse<BOL>> => {
    const response = await api.put(`/api/bol/${id}`, data);
    return response.data;
  },

  reportOSD: async (id: number, formData: FormData): Promise<ApiResponse> => {
    const response = await api.post(`/api/bol/${id}/osd`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteBOL: async (id: number): Promise<ApiResponse> => {
    const response = await api.delete(`/api/bol/${id}`);
    return response.data;
  },

  uploadSignature: async (id: number, signatureFile: File): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('signature', signatureFile);
    
    const response = await api.post(`/api/bol/${id}/signature`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadPhoto: async (id: number, photoFile: File): Promise<FileUploadResponse> => {
    const formData = new FormData();
    formData.append('photo', photoFile);
    
    const response = await api.post(`/api/bol/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (id: number, filename: string): Promise<ApiResponse> => {
    const response = await api.delete(`/api/bol/${id}/file/${filename}`);
    return response.data;
  },
};

// Health check
export const healthAPI = {
  check: async (): Promise<ApiResponse> => {
    const response = await api.get('/api/health');
    return response.data;
  },
};

export default api; 